import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Award, Trophy, Crown, ArrowUpRight, Check } from "lucide-react";

/**
 * TierProgressCard — shows current tier + slim progress bar toward the next tier.
 * Driven by data.tier on /api/affiliate/overview.
 *
 * Canonical design language: white card, dark navy (#0F172A) rounded-square icon
 * containers with white icons, brand-blue (#0055FF) accents only.
 */
const TIER_META = {
  silver:   { name: "Silver",   Icon: Award,  rate: "20% recurring", short: "Starter tier" },
  growth:   { name: "Growth",   Icon: Trophy, rate: "30% recurring", short: "Most active partners" },
  platinum: { name: "Platinum", Icon: Crown,  rate: "40% + bonuses", short: "Top 1%" },
};

const NEXT_PERKS = {
  growth: [
    "30% recurring (up from 20%) — backdated to this month",
    "Weekly Stripe Connect Express payout (instant)",
    "Direct Slack channel with Partner Success",
  ],
  platinum: [
    "40% recurring (up from 30%) — for the lifetime of every account",
    "Quarterly performance bonus pool",
    "£1k/month co-funded paid media for your funnel",
  ],
};

const fmt = (n) => `£${(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

/* Canonical icon container — dark navy square w/ white icon */
const IconChip = ({ Icon, size = "md" }) => {
  const s = size === "sm" ? "size-7" : size === "lg" ? "size-10" : "size-9";
  const i = size === "sm" ? "size-3.5" : size === "lg" ? "size-5" : "size-4";
  return (
    <span className={`inline-grid place-items-center ${s} rounded-lg bg-[#0F172A] text-white shrink-0`}>
      <Icon className={i} strokeWidth={1.6} />
    </span>
  );
};

export default function TierProgressCard({ tier }) {
  if (!tier) return null;
  const current = TIER_META[tier.current];
  const next = tier.next ? TIER_META[tier.next] : null;
  const pct = tier.progress_pct ?? 100;
  const NextIcon = next?.Icon;

  // Platinum reached — top-out card
  if (!next) {
    return (
      <Card className="rounded-xl shadow-soft border-slate-200 bg-white" data-testid="tier-progress-maxed">
        <CardContent className="p-6 flex items-start gap-4">
          <IconChip Icon={Crown} size="lg" />
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Platinum partner</div>
            <div className="mt-1.5 text-[17px] font-semibold text-slate-900 tracking-tight">You've reached the top tier.</div>
            <div className="mt-1.5 text-[13px] text-slate-600">40% recurring commission for the lifetime of every account · quarterly bonus pool · listed on the public leaderboard.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl shadow-soft border-slate-200 bg-white overflow-hidden" data-testid="tier-progress-card">
      <CardContent className="p-6">
        {/* Top row: current tier + view-all */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-3">
            <IconChip Icon={current.Icon} />
            <div className="leading-tight">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Current tier</div>
              <div className="mt-1 text-[17px] font-semibold text-slate-900 tracking-tight">{current.name}</div>
              <div className="text-[12px] text-slate-500 mt-0.5">{current.rate}</div>
            </div>
          </div>
          <Link to="/partners#commission" className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-[#0055FF] transition-colors shrink-0 mt-1" data-testid="tier-progress-view-all">
            All tiers <ArrowUpRight className="size-3" />
          </Link>
        </div>

        {/* Progress meter */}
        <div className="mb-1.5 flex items-end justify-between gap-3">
          <div className="text-[12.5px] text-slate-700">
            <span className="font-mono tabular-nums text-slate-900 font-semibold">{fmt(tier.lifetime_commission)}</span>
            <span className="text-slate-500"> / {fmt(tier.next_threshold)} commission</span>
          </div>
          <div className="text-[12.5px] text-[#0055FF] font-medium inline-flex items-center gap-1.5">
            <NextIcon className="size-3.5 text-[#0055FF]" strokeWidth={1.6} />
            {fmt(tier.remaining)} to {next.name}
          </div>
        </div>
        <div className="relative h-2 rounded-full bg-slate-100 overflow-hidden" data-testid="tier-progress-bar">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-[#0055FF] transition-[width] duration-700 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1.5 text-right text-[10.5px] font-mono text-slate-400 tabular-nums">{pct}%</div>

        {/* Next-tier unlock preview */}
        <div className="mt-5 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2.5 mb-3">
            <IconChip Icon={next.Icon} size="sm" />
            <div className="text-[12px] uppercase tracking-[0.16em] text-slate-700 font-semibold">Unlock at {next.name}</div>
          </div>
          <ul className="space-y-2">
            {(NEXT_PERKS[tier.next] || []).map((perk, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-700 leading-snug">
                <Check className="size-3.5 text-[#0055FF] shrink-0 mt-0.5" strokeWidth={2.4} />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
